import React, { useEffect, useRef } from "react";
import Helmet from "react-helmet";
import { FaEdit, FaPlus, FaTrashAlt } from "react-icons/fa";
import { useSelector } from "react-redux";
import Layout from "../../../components/admin/Layout";
import InputField from "../../../components/ui/form/InputField";
import Table from "../../../components/ui/Table";
import Pagination from "../../../components/ui/Pagination";
import usePaginate from "../../../hooks/api/usePaginate";
import apiRequest from "../../../utils/admin/apiRequest";
import Modal from "../../../components/ui/utils/Modal";
import useModal from "../../../hooks/ui/useModal";
import { formatDate, getFileUrl } from "../../../utils/helper";

import Button from "../../../components/ui/utils/Button";
import useFormInput from "../../../hooks/form/useFormInput";
import CreateSubCategory from "../../../components/admin/category/sub-category/CreateSubCategory";
import EditSubCategory from "../../../components/admin/category/sub-category/EditSubCategory";
import DeleteSubCategory from "../../../components/admin/category/sub-category/DeleteSubCategory";

const SubCategory = () => {
  const {
    currentPage,
    data,
    error,
    fetchData,
    limit,
    loading,
    setPage,
    setSorting,
    sort,
    totalItems,
    totalPages,
    setFilters,
  } = usePaginate({
    apiMethod: apiRequest.getCategory,
    dataKey: "categories",
  });


  

  const { isOpen, openModal, closeModal, actionType } = useModal();
  const handleSort = (field) => {
    const order =
      sort.sortField === field && sort.sortOrder === "asc" ? "desc" : "asc";
    setSorting({ sortField: field, sortOrder: order });
  };
  const siteName = useSelector(
    (state) => state.globalState?.siteSetting?.site_settings?.site_name
  );

  const searchInitialValues = { name: "" };
  const { values, handleChange, setValues } = useFormInput(searchInitialValues);
  const debounceRef = useRef(null);
  useEffect(() => {
    if (values.name !== undefined) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const filters = {};
        filters.parentCategory = {$ne : null};
        if (values.name) {
          filters.name = { $regex: values.name, $options: "i" };
        }
        setFilters(filters);
      }, 500);
      return () => clearTimeout(debounceRef.current);
    }
  }, [values.name],[]);
  
  return (
    <Layout>
      <Helmet>
        <title>Category - {siteName ?? ''}</title>
      </Helmet>
      <div className="p-4 bg-white rounded">
        <div className="flex items-center gap-1 py-2">
          <Button
            onClick={() => openModal({ type: "add" })}
            variant="primary"
            isLoading={false}
          >
            <div className="flex items-center justify-center gap-3">
              <FaPlus />
              <p>Add New</p>
            </div>
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-2 my-2">
          <InputField
            name="name"
            placeholder="e.g. Smartphone"
            value={values.name}
            onChange={handleChange}
          />
          <button
            className="py-1 text-white rounded bg-emerald-600 hover:bg-emerald-800"
            onClick={() => setValues(searchInitialValues)}
          >
            Clear
          </button>
        </div>
        <Table
          headers={[
            { label: "Image", key: "image", sorting: false },
            { label: "Main Category", key: "maincategory", sorting: true },
            { label: "Category", key: "category", sorting: true },
            { label: "Sub Category", key: "subcategory", sorting: true },
            { label: "Updated At", key: "updatedAt", sorting: true },
            { label: "Created At", key: "createdAt", sorting: true },
            {
              label: "Action",
              key: "action",
              sorting: false,
            },
          ]}
          loading={loading}
          handleSort={handleSort}
          sort={sort}
          error={error}
          cols={6}
          renderRow={
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.map((category) => (
                <tr
                  key={category._id}
                  className="transition-colors hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <div className="w-16 h-16 overflow-hidden">
                      <img
                        src={getFileUrl(category?.image)}
                        alt={category?.name}
                        className="object-contain w-full h-full border border-gray-300 rounded"
                      />
                    </div>
                  </td>

                  <td className="px-3 py-3">{category?.mainCategory?.name}</td>
                  <td className="px-3 py-3">{category.name}</td>
                  <td className="px-3 py-3">{category?.parentCategory?.name}</td>

                  <td className="px-3 py-3">
                    {formatDate(category.updatedAt)}
                  </td>
                  <td className="px-3 py-3">
                    {formatDate(category.createdAt)}
                  </td>

                  <td className="px-3 py-3">
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() =>
                          openModal({
                            type: "edit",
                            data: { state: category },
                          })
                        }
                        className="text-primary"
                        title="Edit"
                      >
                        <FaEdit size={18} />
                      </button>
                      <button
                        onClick={() =>
                          openModal({
                            type: "delete",
                            data: { state: category },
                          })
                        }
                        className="text-red-500"
                        title="Delete"
                      >
                        <FaTrashAlt size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          }
        />
        {totalPages > 1 && (
          <Pagination
            pagination={{
              currentPage,
              totalPages,
              totalItems: totalItems,
              perPage: limit,
            }}
            handlePageChange={setPage}
            loading={loading}
          />
        )}
      </div>
      <Modal isOpen={isOpen} onClose={closeModal} size="lg"   overflow={"overflow-y-scroll"}>
        {actionType?.type === "add" && (
          <CreateSubCategory closeModal={closeModal} callback={fetchData} />
        )}
        {actionType?.type === "edit" && (
          <EditSubCategory
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
        {actionType?.type === "delete" && (
          <DeleteSubCategory
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default SubCategory;
